/**
 * One socket, for every conversation.
 *
 * Not one per conversation: that would multiply authentication, backoff, resume
 * and reconnect storms by however many rooms somebody is in, and a person in
 * forty channels would open forty connections to say nothing.
 *
 * ── What this object knows, and what it does not ────────────────────────────
 * It knows how to be connected: open, `hello`, heartbeat, backoff, close codes.
 * It does not know what a conversation is, what a message is, or what any
 * payload says. The cursors in `hello` come from `onHello`, which the store
 * answers — so the one fact the socket needs about the world arrives at the one
 * moment it is needed, and there is no second copy of the store's state here to
 * go stale.
 *
 * ── Why everything is injectable ────────────────────────────────────────────
 * `open`, `timers` and `random` are constructor options because the reconnect
 * path is the part of this file most likely to be wrong and the hardest to
 * reach: it needs a server that drops a socket. A fake `WebSocket` and a fake
 * clock reach it in a millisecond, which is what `socket.test.ts` does.
 *
 * ── The cookie is the credential ────────────────────────────────────────────
 * The URL is relative and same-origin, so `mauvely_session` rides the upgrade
 * on its own. A browser cannot set a header on an upgrade — which is why the Qt
 * client's `Authorization: Bearer` has no counterpart here, and why
 * relay-server checks `Origin` on the upgrade instead. Naming relay-server's
 * hostname here would break the cookie and reintroduce a problem that is
 * currently solved by construction.
 */

import {
  PING, PROTOCOL_VERSION, backoffDelayMs, closeAction, encodeFrame, parseFrame
} from './frames';
import type { ClientFrame, ServerFrame, SyncFrame, WatchEntry } from './frames';

/** The slice of `WebSocket` this file uses. A fake implements it in ten lines. */
export interface WebSocketLike {
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason?: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface Timers {
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(handle: number): void;
  setInterval(fn: () => void, ms: number): number;
  clearInterval(handle: number): void;
}

/** The real ones, wrapped so the return type is a number in both node and the
 *  browser — node's timers are objects, and `noImplicitAny` will not have it. */
export const REAL_TIMERS: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms) as unknown as number,
  clearTimeout: (handle) => clearTimeout(handle),
  setInterval: (fn, ms) => setInterval(fn, ms) as unknown as number,
  clearInterval: (handle) => clearInterval(handle)
};

/**
 * `waiting` is between attempts and `stopped` is the end of it — a revoked
 * session or a device connected elsewhere, neither of which a retry can fix.
 * Two states rather than one because a person waiting deserves to know which
 * one they are looking at.
 */
export type SocketState = 'idle' | 'connecting' | 'online' | 'waiting' | 'stopped';

export interface SocketOptions {
  deviceId: string;
  /** Answers `hello`'s watch list: every conversation and the highest seq held.
   *  Asked at every open, so a resume carries what the store learnt last time. */
  onHello: () => WatchEntry[];
  /** Every frame but `pong`, in arrival order. */
  onFrame: (frame: ServerFrame) => void;
  onState?: (state: SocketState, detail?: { code?: number; reason?: string }) => void;
  /** Reported rather than thrown: `4002` and `4003` are the server refusing
   *  something this client sent, and the person on the screen is owed the
   *  message. */
  onRefused?: (code: number, reason: string) => void;
  open?: (url: string) => WebSocketLike;
  /** The upgrade's URL. Defaults to the same-origin one, which needs
   *  `window.location` — so a test passes its own rather than a DOM. */
  url?: string;
  timers?: Timers;
  random?: () => number;
}

const DEVICE_KEY = 'relay.deviceId';

/**
 * This browser's device id, minted once and kept.
 *
 * A device is **one connection**: opening a second for the same id closes the
 * first with `4004`. So the id has to survive a reload — two tabs are already
 * two connections fighting, and a fresh id per load would leave a trail of dead
 * sockets on every refresh. It is minted in `localStorage`, and where that
 * throws (a private window with site data blocked) the session gets a
 * one-time id, which is worse than persisting and better than not connecting.
 *
 * The shape is the server's `^[A-Za-z0-9_-]{1,64}$`; anything else is refused
 * at the upgrade with `invalid_device`.
 */
export function deviceId(): string {
  try {
    const held = localStorage.getItem(DEVICE_KEY);
    if (held && /^[A-Za-z0-9_-]{1,64}$/.test(held)) return held;
  } catch {
    // Blocked or unavailable. Fall through and mint one for this page.
  }
  const fresh = mintDeviceId();
  try {
    localStorage.setItem(DEVICE_KEY, fresh);
  } catch {
    // Same again. The id is still good for this connection.
  }
  return fresh;
}

function mintDeviceId(): string {
  const bytes = new Uint8Array(12);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  let hex = '';
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');
  return `web_${hex}`;
}

/** The upgrade, same-origin. `ws:` on http, `wss:` on https — a page served
 *  over TLS may not open a plain socket, and the browser fails it silently
 *  enough that it reads as the server being down. */
export function socketUrl(device: string): string {
  const { protocol, host } = window.location;
  const scheme = protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${host}/api/relay/socket?device=${encodeURIComponent(device)}`;
}

export class RelaySocket {
  private readonly options: SocketOptions;
  private readonly timers: Timers;
  private readonly random: () => number;
  private readonly open: (url: string) => WebSocketLike;

  private ws: WebSocketLike | null = null;
  private state: SocketState = 'idle';
  private attempt = 0;
  private retry: number | null = null;
  private beat: number | null = null;
  /** Set by `stop()`, and the only thing that makes a close final. */
  private stopped = false;

  constructor(options: SocketOptions) {
    this.options = options;
    this.timers = options.timers ?? REAL_TIMERS;
    this.random = options.random ?? Math.random;
    this.open = options.open ?? ((url) => new WebSocket(url) as unknown as WebSocketLike);
  }

  currentState(): SocketState {
    return this.state;
  }

  /** Idempotent: a second call while connected does nothing, which is what
   *  makes "start it wherever you notice it is needed" safe. */
  start(): void {
    this.stopped = false;
    if (this.ws || this.state === 'connecting') return;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearRetry();
    this.clearBeat();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.close(1000, 'client');
    }
    this.setState('idle');
  }

  /** True when the frame went out. False is not an error to report: an
   *  ephemeral dropped because the socket is down is the ephemeral lane working
   *  as designed, and the caller decides whether that matters. */
  send(frame: ClientFrame): boolean {
    if (!this.ws || this.state !== 'online') return false;
    try {
      this.ws.send(encodeFrame(frame));
      return true;
    } catch {
      return false;
    }
  }

  // ── the connection ────────────────────────────────────────────────────────

  private connect(): void {
    this.setState('connecting');
    let ws: WebSocketLike;
    try {
      ws = this.open(this.options.url ?? socketUrl(this.options.deviceId));
    } catch {
      // A constructor that throws is a URL the browser refused. Treat it as a
      // close: it is the same situation, and the backoff is the same answer.
      this.scheduleRetry();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      // Online before `hello` goes out, because `send` checks the state.
      this.setState('online');
      this.send({
        t: 'hello',
        p: PROTOCOL_VERSION,
        device: this.options.deviceId,
        watch: this.options.onHello()
      });
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      const frame = parseFrame(event.data);
      if (!frame) return;
      // The first `sync` is what proves the connection works, rather than the
      // open — a socket that opens and is closed at `hello` would otherwise
      // reset the backoff and hammer.
      if (frame.t === 'sync') {
        this.attempt = 0;
        this.startBeat((frame as SyncFrame).heartbeat);
      }
      this.options.onFrame(frame);
    };

    ws.onerror = () => {
      // Nothing. A browser fires `error` before `close` and tells you nothing
      // either time; `close` carries the code, and the code is the whole
      // decision.
    };

    ws.onclose = (event) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearBeat();
      if (this.stopped) {
        this.setState('idle');
        return;
      }
      const action = closeAction(event.code);
      if (action === 'report') {
        this.options.onRefused?.(event.code, event.reason ?? '');
      }
      if (action === 'reconnect') {
        this.scheduleRetry(event);
        return;
      }
      this.setState('stopped', { code: event.code, reason: event.reason ?? '' });
    };
  }

  private scheduleRetry(event?: { code: number; reason?: string }): void {
    this.clearRetry();
    const delay = backoffDelayMs(this.attempt, this.random);
    this.attempt += 1;
    this.setState('waiting', event ? { code: event.code, reason: event.reason ?? '' } : undefined);
    this.retry = this.timers.setTimeout(() => {
      this.retry = null;
      if (this.stopped) return;
      this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retry === null) return;
    this.timers.clearTimeout(this.retry);
    this.retry = null;
  }

  /**
   * The heartbeat, at whatever `sync` said.
   *
   * Read from the frame rather than hard-coded: the value is the server's and
   * it is echoed there for exactly this reason. 45 seconds because Cloudflare
   * drops an idle socket at 60, and the runtime answers `ping` without waking
   * the Durable Object — which is the only reason one this frequent is
   * affordable at all.
   */
  private startBeat(seconds: number): void {
    this.clearBeat();
    const interval = Number.isFinite(seconds) && seconds > 0 ? seconds : 45;
    this.beat = this.timers.setInterval(() => {
      if (!this.ws || this.state !== 'online') return;
      try {
        this.ws.send(PING);
      } catch {
        // The close handler is about to run. Nothing useful to do here.
      }
    }, interval * 1000);
  }

  private clearBeat(): void {
    if (this.beat === null) return;
    this.timers.clearInterval(this.beat);
    this.beat = null;
  }

  private setState(state: SocketState, detail?: { code?: number; reason?: string }): void {
    if (this.state === state && !detail) return;
    this.state = state;
    this.options.onState?.(state, detail);
  }
}
